import { useState } from "react";
import { useTranslation } from "react-i18next";
import jmespath from "jmespath";
import { useFetch } from "../../../../hooks/api";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Container as Grid, Row, Col, Nav, Badge } from "react-bootstrap";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { Link, createFileRoute } from "@tanstack/react-router";
import { DatesProvider } from "../../../../dates/DatesContext";
import { useDates } from "../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { useMemo } from "react";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  parseFormattedCurrency,
  parseFormattedNumber,
  parseFormattedPercentage,
} from "../../../../utils/formatNumber";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { getURLPrefix } from "../../../../utils/getUrlPrefix";
import ReactTable from "react-table-6";
import useColumnSelector from "../../../../hooks/useColumnSelector";
import { filterRanges, generateFilterMarkup, generateRanges } from "../../../../utils/rangeDropdownFilter";
import ReactTablePagination from "../../../../components/custom/growth/ProductsCVTablePagination";
import { DownloadV4Report } from "../../../../modules/perf_reports/DownloadReport";
import Loading from "../../../../components/core/blocks/Loading";
import DetailedMetric from "../../../../components/core/blocks/DetailedMetric";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { buttonRecipe } from "../../../../components/core/basic/Button";
import { css, cx } from "../../../../../styled-system/css";
import NoImage from "../../../../assets/images/utility/no_image.png";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC } from "../../../../utils/dashboardLinks";
import { useAppSelector } from "../../../../redux/hooks";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useGetV4ProductReportTotals } from "../../../../modules/perf_reports/api/product/v4ProductReportTotals";
import { useGetV4ProductReportByDate } from "../../../../modules/perf_reports/api/product/v4ProductReportByDate";
import { useV4ProductReportExport } from "../../../../modules/perf_reports/api/product/v4ProductReportExport";

const URL_PREFIX = getURLPrefix();

const LAUNCH_STATES = {
  PREPARING: {
    id: "1",
    value: "PREPARING",
    name: "Preparing",
    color: "#A78DC2",
  },
  INTRODUCED: {
    id: "2",
    value: "INTRODUCED",
    name: "Introduced",
    color: "#0FC398",
  },
  LAUNCHED: {
    id: "3",
    value: "LAUNCHED",
    name: "Launched",
    color: "red",
  },
};

const LaunchCell = (props) => {
  const { products, id } = props.original;
  const image = `${URL_PREFIX}/api/productimage/?product=${products[0]}`;
  return (
    <div className="d-flex align-items-center">
      <div
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          flexShrink: 0,
          width: 50,
          height: 50,
          marginRight: "10px",
          background: `url(${image}) no-repeat center / contain, url(${NoImage}) no-repeat center / contain`,
        }}
        draggable="false"
      />
      <Link
        to="/user/productlaunch/launches/$id"
        params={{ id: String(id) }}
        style={{
          color: trellisPalette[13],
          textAlign: "center",
          float: "center",
          textDecoration: "underline",
          fontWeight: 500,
          fontSize: "1.5rem",
          cursor: "pointer",
        }}
      >
        {props.value}
      </Link>
    </div>
  );
};

const col = (header, type, key, options) => ({
  header,
  type,
  key,
  options,
});

const makeColumn = (data, marketPlace, date, dateFormatter) => (header, key, type, options) => {
  const { hideFilter } = options ?? {};

  const defaultAccessor = (p) => jmespath.search(p, key);
  const accessor = options?.accessor ?? defaultAccessor;
  const defaultFormat = ({ value }) => value;
  const fmt =
    options?.formatter ??
    {
      currency: ({ value }) =>
        value == null ? "" : (
          formatCurrency(value, {
            marketPlace: marketPlace.marketPlace,
            decimalPlaces: options?.decimalPlaces ?? 0,
          })
        ),
      number: ({ value }) => (value == null ? "" : formatNumber(value, { decimalPlaces: 1 })),
      percent: ({ value }) =>
        value == null ? "" : (
          formatPercent(value, {
            decimalPlaces: 0,
          })
        ),
      date: ({ value }) => (value == null ? "" : dateFormatter.format(new Date(value))),
      launch: LaunchCell,
      percentDiff: ({ value }) => (
        <span
          style={{
            color: value > 0 ? trellisPalette[12] : trellisPalette[17],
          }}
        >
          {value == null ?
            ""
          : formatPercent(value, {
              decimalPlaces: 0,
              addPlus: true,
            })
          }
        </span>
      ),
    }?.[type] ??
    defaultFormat;
  const parser =
    {
      currency: parseFormattedCurrency,
      number: parseFormattedNumber,
      percent: parseFormattedPercentage,
    }?.[type] ?? defaultFormat;

  // Handle percent filter formatting
  if (type === "percent") {
    data = data?.map((row) => {
      Object.keys(row).forEach((propertyKey) => {
        if (propertyKey === key) {
          row["formatted_" + propertyKey] = row[propertyKey] * 100;
        }
      });

      return row;
    });
  }

  return {
    Header: header,
    id: key,
    accessor,
    width: options?.width ? (options?.mobileWidth ?? 100) : 125,
    height: 30,
    className: options?.className ?? "text-end",
    headerClassName: options?.className ?? "text-end",
    Cell: (props) => {
      if (options?.showDot ?? true) {
        const compareKey = options?.compareKey;
        if (compareKey) {
          const compareValue = jmespath.search(props.original, compareKey);
          return (
            <div>
              {fmt({ ...props, date })}
              <br />
              <span
                style={{
                  color: "#666",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                }}
              >
                {compareValue === null ?
                  ""
                : <>
                    <span
                      style={{
                        fontSize: "1rem",
                        fontWeight: "normal",
                      }}
                    >
                      {options.compareType}{" "}
                    </span>
                    {fmt({
                      ...props,
                      value: compareValue,
                      date,
                    })}
                  </>
                }
              </span>
            </div>
          );
        }
      }

      return fmt({ ...props, date });
    },

    style: { height: "60px" },
    filterMethod: (filter, row) => {
      const formattedPercentAccessor = (p) => p[`formatted_${key}`];
      const value = type === "percent" ? formattedPercentAccessor(row._original) : accessor(row._original);
      return filterRanges(filter, value, parser, marketPlace.marketPlace);
    },
    Filter: ({ filter, onChange }) => {
      if (hideFilter) {
        return null;
      }

      return generateFilterMarkup(filter, onChange, key, "range", type, {
        ranges: generateRanges(
          data ?? [],
          5,
          type === "percent" ? `formatted_${key}` : key,
          "number",
          marketPlace.marketPlace,
          true,
        ),
        marketPlace: marketPlace.marketPlace,
      });
    },
  };
};

const Table = ({ stats, marketPlace, columnSpecs, date, dateFormatter }) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const data = useMemo(() => {
    return stats
      ?.filter((launch) => {
        if (searchFilter) {
          const searchable = `${launch?.name}`;
          return searchable.toLowerCase()?.indexOf(searchFilter.toLowerCase()) > -1;
        }
        return true;
      })
      ?.map((d) => {
        return {
          ...d,
          goal_metrics: {
            ...d.goal_metrics,
            acos: d.goal_metrics.acos / 100,
            margin: d.goal_metrics.margin / 100,
          },
        };
      });
  }, [searchFilter, stats]);

  const createColumn = makeColumn(data, marketPlace, date, dateFormatter);
  const [columns, columnSelectorProps] = useColumnSelector(
    () => columnSpecs.map((c) => createColumn(c.header, c.key, c.type, c.options)),
    [columnSpecs, stats],
  );
  const columnOrder = columns.map((col) => col.id);
  return (
    <ReactTable
      showPaginationBottom={false}
      showPaginationTop
      PaginationComponent={ReactTablePagination}
      columns={columns}
      data={data}
      minRows={0}
      defaultPageSize={10}
      className="-highlight"
      filterable={showFilters}
      state={columnOrder}
      getTheadFilterProps={(props) => {
        if (clearFilters) {
          props.filtered.forEach((filter) => {
            filter.value = "all";
          });
          setClearFilters(false);
        }
        return { style: { display: "flex" } };
      }}
      getPaginationProps={() => ({
        setShowFilters: setShowFilters,
        updateSearch: setSearchFilter,
        clearFilters: () => {
          setSearchFilter("");
          setClearFilters(true);
        },
        hideFilters: false,
        showFilters: showFilters,
        clearSearchTermFilters: () => setSearchFilter(""),
        searchTerm: searchFilter,
        table: "plan-products",
        style: { marginBottom: "2rem" },
        carouselLayout: false,
        disableSearchFocus: true,
        columnSelectorProps: columnSelectorProps,
      })}
    />
  );
};

const LaunchList = () => {
  const { t } = useTranslation(["merchandising"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { date, start, end } = useDates();
  const longDate = useDateFormatter("long");
  const shortDate = useDateFormatter("short");
  const [tab, setTab] = useState(LAUNCH_STATES.INTRODUCED.value);
  const userCanUpdateProducts = useSimpleCan("update", "Product");
  const userCanExportProducts = useSimpleCan("export", "Product");

  const { data: launchData } = useFetch(
    ["product_launch_summary"],
    `/merchandise/product_launch`,
    {},
    {
      default: [],
      select: (d) => d.data?.results ?? [],
    },
  );

  const targetFormatter = (v) =>
    formatCurrency(v, {
      marketPlace: marketPlace.marketPlace,
      compact: true,
    });

  const launchStateData = useMemo(() => {
    return launchData?.filter((launch) => launch.state.toLowerCase() === tab.toLowerCase());
  }, [launchData, tab]);

  // Calculate summary stat data
  const preparingLaunchesRequiringActionCount = launchData?.filter(
    (l) => l?.stats?.action_required && l?.state?.toLowerCase() === LAUNCH_STATES.PREPARING.value.toLowerCase(),
  )?.length;
  const activeLaunchesRequiringActionCount = launchData?.filter(
    (l) => l?.stats?.action_required && l?.state?.toLowerCase() === LAUNCH_STATES.INTRODUCED.value.toLowerCase(),
  )?.length;
  const completedLaunchesRequiringActionCount = launchData?.filter(
    (l) => l?.stats?.action_required && l?.state?.toLowerCase() === LAUNCH_STATES.LAUNCHED.value.toLowerCase(),
  )?.length;
  const bestsellerCount = launchData?.reduce((prev, current) => prev + current?.stats?.bestseller_count, 0);
  const bestsellerRevenue = launchData?.reduce((prev, current) => prev + current?.stats?.bestseller_sales, 0);
  const clearanceCount = launchData?.reduce((prev, current) => prev + current?.stats?.clearance_count, 0);
  const metGoalsCount = launchData?.reduce((prev, current) => {
    if (
      current.state.toUpperCase() === LAUNCH_STATES.LAUNCHED.value &&
      (current.stats.acos * 100 < current.goal_metrics.acos || !current.goal_metrics.acos) &&
      (current.stats.margin > current.goal_metrics.margin || !current.goal_metrics.margin) &&
      (current.stats.rating >= current.goal_metrics.min_rating || !current.goal_metrics.min_rating)
    ) {
      return prev + 1;
    }
    return prev;
  }, 0);
  const clearanceRevenue = launchData?.reduce((prev, current) => prev + current?.stats?.clearance_sales, 0);
  const inProgressRevenue = launchData?.reduce((prev, current) => {
    if (current.state.toUpperCase() === LAUNCH_STATES.INTRODUCED.value) {
      return prev + current?.stats?.total_sales;
    }
    return prev;
  }, 0);
  const metGoalsRevenue = launchData?.reduce((prev, current) => {
    if (
      current.state.toUpperCase() === LAUNCH_STATES.LAUNCHED.value &&
      (current.stats.acos * 100 < current.goal_metrics.acos || !current.goal_metrics.acos) &&
      (current.stats.margin > current.goal_metrics.margin || !current.goal_metrics.margin) &&
      (current.stats.rating >= current.goal_metrics.min_rating || !current.goal_metrics.min_rating)
    ) {
      return prev + current?.stats?.total_sales;
    }
    return prev;
  }, 0);

  const readyToLaunchCount = launchData?.[0]?.stats?.ready_to_launch_count;

  const NPI_FILTER = [{ id: "npi_enrolled", value: { op: "=", value: true } }];

  const totalsOptionsGetter = useGetV4ProductReportTotals();
  const {
    data: totalsData,
    isLoading: totalsLoading,
    isError: totalsError,
  } = useQuery({
    ...totalsOptionsGetter({ filters: NPI_FILTER }),
    placeholderData: keepPreviousData,
  });

  const byDateOptionsGetter = useGetV4ProductReportByDate();
  const {
    data: byDateData,
    isLoading: byDateLoading,
    isError: byDateError,
  } = useQuery({
    ...byDateOptionsGetter({ filters: NPI_FILTER }),
    placeholderData: keepPreviousData,
  });

  const isLoading = totalsLoading || byDateLoading;
  const isError = totalsError || byDateError;

  const exportMutation = useV4ProductReportExport(NPI_FILTER);

  const breadCrumbItems = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    { name: t(($) => $.merchandising["New Product Launch"]), href: "/user/productlaunch" },
  ];

  const [metrics, launchStateMetrics, preparingLaunchColumns, launchTableColumns] = useMemo(() => {
    if (totalsData) {
      return [
        [
          getMetricSpec("sales"),
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          getMetricSpec("units"),
          getMetricSpec("rating"),
          getMetricSpec("ratings_total"),
          getMetricSpec("bsr"),
          getMetricSpec("page_views"),
          getMetricSpec("margin"),
          getMetricSpec("roas"),
        ],
        [
          {
            id: "ready_to_launch_products",
            name: t(($) => $.merchandising["Products Ready To Be Launched"]),
            color: trellisPalette[6],
            format: "number",
            accessor: () => readyToLaunchCount,
            actual: readyToLaunchCount,
          },

          {
            id: "in_progress",
            name: t(($) => $.merchandising["In Progress"]),
            color: trellisPalette[2],
            format: "number",
            toolTip: t(($) => $.merchandising["The number of active product launches"]),
            accessor: () => launchData?.filter((l) => l.state.toUpperCase() === LAUNCH_STATES.INTRODUCED.value)?.length,
            actual: launchData?.filter((l) => l.state.toUpperCase() === LAUNCH_STATES.INTRODUCED.value)?.length,
            target: inProgressRevenue,
            targetFormatter: targetFormatter,
            targetLabel: t(($) => $.merchandising["earned "]),
          },

          {
            id: "met_goals",
            name: t(($) => $.merchandising["Met Goals"]),
            color: trellisPalette[14],
            format: "number",
            accessor: () => metGoalsCount,
            actual: metGoalsCount,
            target: metGoalsRevenue,
            targetFormatter: targetFormatter,
            targetLabel: t(($) => $.merchandising["earned "]),
          },
          {
            id: "best_sellers",
            name: t(($) => $.merchandising["Best Seller Products"]),
            color: trellisPalette[15],
            format: "number",
            accessor: () => bestsellerCount,
            actual: bestsellerCount,
            target: bestsellerRevenue,
            targetFormatter: targetFormatter,
            targetLabel: t(($) => $.merchandising["earned "]),
          },
          {
            id: "clearance_products",
            name: t(($) => $.merchandising["Clearance Products"]),
            color: trellisPalette[4],
            format: "number",
            accessor: () => clearanceCount,
            actual: clearanceCount,
            target: clearanceRevenue,
            targetFormatter: targetFormatter,
            targetLabel: t(($) => $.merchandising["earned "]),
          },
        ],
        // PREP STATE
        [
          col(
            t(($) => $.merchandising["Launch"]),
            "launch",
            "name",
            {
              className: "text-start font-weight-bold",
              width: 350,
              mobileWidth: 250,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
            },
          ),
          col(
            t(($) => $.merchandising["Start Date"]),
            "date",
            "start_date",
            { hideFilter: true },
          ),
          col(
            t(($) => $.merchandising["End Date"]),
            "date",
            "end_date",
            { hideFilter: true },
          ),
          col(
            t(($) => $.merchandising["Target Sales"]),
            "currency",
            "exit_criteria.sales",
          ),
          col(
            t(($) => $.merchandising["Target Units"]),
            "number",
            "exit_criteria.units",
          ),
          col(
            t(($) => $.merchandising["Target # of Ratings"]),
            "number",
            "exit_criteria.num_of_ratings",
          ),
          col(
            t(($) => $.merchandising["Optimal ACOS"]),
            "percent",
            "goal_metrics.acos",
            {
              divideBy100: true,
            },
          ),
          col(
            t(($) => $.merchandising["Target Margin"]),
            "percent",
            "goal_metrics.margin",
            {
              divideBy100: true,
            },
          ),
          col(
            t(($) => $.merchandising["Target Min Rating"]),
            "number",
            "goal_metrics.min_rating",
          ),
        ],
        // ACTIVE AND LAUNCHED STATE COLS
        [
          col(
            t(($) => $.merchandising["Launch"]),
            "launch",
            "name",
            {
              className: "text-start font-weight-bold",
              width: 350,
              mobileWidth: 250,
              showDot: false,
              fixedLeft: true,
              hideFilter: true,
              isStatic: true,
            },
          ),
          col(
            t(($) => $.merchandising["Start Date"]),
            "date",
            "start_date",
            { hideFilter: true },
          ),
          col(
            t(($) => $.merchandising["End Date"]),
            "date",
            "end_date",
            { hideFilter: true },
          ),
          col(
            t(($) => $.merchandising["Sales"]),
            "currency",
            "stats.sales",
          ),
          col(
            t(($) => $.merchandising["Ad Spend"]),
            "currency",
            "stats.cost",
            { noColor: true },
          ),
          col(
            t(($) => $.merchandising["Units"]),
            "number",
            "stats.units",
          ),
          col(
            t(($) => $.merchandising["Average Rating"]),
            "number",
            "stats.rating",
          ),
          col(
            t(($) => $.merchandising["# of Ratings"]),
            "number",
            "stats.ratings_count",
          ),
        ],
      ];
    }
    return [[], [], [], [], [], []];

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [totalsData, t]);

  return (
    <Grid fluid className="py-3" style={{ minHeight: "calc(100vh - 150px)" }}>
      <Row style={{ marginTop: "-10px", marginBottom: "1rem" }}>
        <Col xs={12}>
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {isLoading && <Loading />}
          {isError && <p>{t(($) => $.merchandising["Error Loading Dashboard"])}</p>}
          {totalsData && (
            <MetricsCard
              title={t(($) => $.merchandising["New Product Launch"])}
              totals={{
                data: totalsData?.data,
                loading: isLoading,
              }}
              timeline={{
                data: byDateData?.data || [],
                loading: isLoading,
              }}
              context={"product"}
              metrics={metrics}
              showMetricsSelector={true}
              initialSelectedMetric={["sales", "units", "rating"]}
              byline={
                <>
                  {longDate.format(start.toDate())}
                  {" - "}
                  {longDate.format(end.toDate())}
                  {isLoading && <i className="fa fa-spinner fa-spin"></i>}
                </>
              }
            >
              <Row>
                <Col xs={12} md={8} lg={10} xl={11}>
                  <Row>
                    {launchStateMetrics?.map((m, i) => (
                      <Col xs={12} md={6} lg={2} key={m.id} className={`mb-4 ${i % 2 === 0 ? "ps-2" : "pe-2"}`}>
                        <div>
                          <div>
                            <DetailedMetric center metric={m} data={totalsData?.data} showTarget={false} />
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
                {userCanUpdateProducts && (
                  <Col
                    xs={12}
                    md={4}
                    lg={2}
                    xl={1}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      paddingLeft: "1rem",
                    }}
                  >
                    <Link
                      to="/user/productlaunch/new"
                      className={cx(buttonRecipe(), css({ height: "30%", marginRight: "xxlarge" }))}
                    >
                      {t(($) => $.merchandising["Launch Product"])}
                    </Link>
                  </Col>
                )}
                <Col xs={12} className="mt-1 ph-5 mb-5 border-bottom fs-standard">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    <Nav.Item>
                      <Nav.Link eventKey={LAUNCH_STATES.PREPARING.value}>
                        {t(($) => $.merchandising["Planning"])}{" "}
                        {preparingLaunchesRequiringActionCount ?
                          <Badge className={`badge-primary px-2`} style={{ marginLeft: ".5rem" }} pill>
                            {preparingLaunchesRequiringActionCount}
                          </Badge>
                        : ""}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey={LAUNCH_STATES.INTRODUCED.value}>
                        {t(($) => $.merchandising["Active"])}{" "}
                        {activeLaunchesRequiringActionCount ?
                          <Badge className={`badge-primary px-2`} style={{ marginLeft: ".5rem" }} pill>
                            {activeLaunchesRequiringActionCount}
                          </Badge>
                        : ""}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey={LAUNCH_STATES.LAUNCHED.value}>
                        {t(($) => $.merchandising["Completed"])}{" "}
                        {completedLaunchesRequiringActionCount ?
                          <Badge className={`badge-primary px-2`} style={{ marginLeft: ".5rem" }} pill>
                            {completedLaunchesRequiringActionCount}
                          </Badge>
                        : ""}
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Table
                    stats={launchStateData}
                    marketPlace={marketPlace}
                    columnSpecs={tab === LAUNCH_STATES.PREPARING.value ? preparingLaunchColumns : launchTableColumns}
                    date={date}
                    dateFormatter={shortDate}
                  />
                </Col>
              </Row>
              {userCanExportProducts && (
                <Row>
                  <Col xs={12} className="py-5">
                    <DownloadV4Report downloadMutation={exportMutation} />
                  </Col>
                </Row>
              )}
            </MetricsCard>
          )}
        </Col>
      </Row>
    </Grid>
  );
};

const LaunchDashboard = () => {
  return (
    <DatesProvider>
      <LaunchList />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/productlaunch/")({
  component: LaunchDashboard,
});
